import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { useState, type ReactNode } from "react";

import { auth } from "../FirebaseConfig";
import { useAuth } from "../lib/auth-context";
import { useViewAsUser } from "../lib/view-as";

/**
 * App shell with header + nav, used as the outer layout for both
 * admin and user routes.
 *
 * Desktop (md+): horizontal tabs in the sticky header.
 * Mobile (<md): fixed bottom bar with up to 4 primary tabs plus a
 *   "Más" overflow sheet for the rest. The choice of "primary" is
 *   the daily-action subset; everything else lives in Más so the bar
 *   stays uncluttered.
 */
export function AppShell() {
  const { usuario } = useAuth();
  const realIsAdmin = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
  const [viewAsUser, setViewAsUser] = useViewAsUser();
  // Admins can flip to "vista usuario" — the UI then pretends they are
  // a regular user (user-only nav, admin routes blocked). The DB
  // identity is untouched, so Firestore writes still succeed under
  // admin privileges; the toggle is purely a UX simulation.
  const isAdmin = realIsAdmin && !viewAsUser;
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  async function handleSignOut() {
    await signOut(auth);
    navigate("/", { replace: true });
  }
  function enterViewAsUser() {
    setViewAsUser(true);
    navigate("/mis-vuelos", { replace: true });
  }
  function exitViewAsUser() {
    setViewAsUser(false);
    navigate("/admin", { replace: true });
  }

  const navClaude: NavSpec = { to: "/claude", label: "Claude", img: "/claude-logo.svg" };
  const adminPrimary: NavSpec[] = [
    { to: "/admin", label: "Resumen", icon: "📊", end: true },
    { to: "/admin/flights", label: "Vuelos", icon: "✈️" },
    { to: "/solicitar", label: "Solicitud", icon: "📝" },
    { to: "/admin/notifications", label: "Notificaciones", icon: "📨" },
  ];
  const adminSecondary: NavSpec[] = [
    { to: "/admin/users", label: "Usuarios" },
    { to: "/admin/passengers", label: "Pasajeros" },
    { to: "/admin/accounts", label: "Cuentas" },
    { to: "/admin/aircraft", label: "Aviones" },
    { to: "/admin/locations", label: "Aeropuertos" },
    { to: "/admin/distances", label: "Distancias" },
    navClaude,
  ];

  // User nav specs (shared between desktop and mobile, but ordered
  // differently — see below).
  const navMisVuelos: NavSpec = { to: "/mis-vuelos", label: "Vuelos", icon: "✈️", end: true };
  const navCalendario: NavSpec = { to: "/admin/calendar", label: "Calendario", icon: "📅" };
  const navUserPasajeros: NavSpec = { to: "/admin/passengers", label: "Pasajeros", icon: "👥" };
  const navSolicitudHL: NavSpec = { to: "/solicitar", label: "Solicitud", icon: "📝", highlight: true };
  // Desktop user nav: Solicitud stays last as the call-to-action.
  const userNav: NavSpec[] = [navMisVuelos, navCalendario, navUserPasajeros, navClaude, navSolicitudHL];
  // Mobile user bottom bar: Solicitud first (most-used action), then the rest.
  const userMobile: NavSpec[] = [navSolicitudHL, navMisVuelos, navCalendario, navUserPasajeros, navClaude];
  // All user destinations fit on the mobile bottom bar, so no overflow sheet.
  const userSecondary: NavSpec[] = [];

  // Desktop nav = full flat list, in order.
  const desktopNav: NavSpec[] = isAdmin
    ? [...adminPrimary, ...adminSecondary]
    : [...userNav, ...userSecondary];
  // Mobile bottom bar = primary subset; "Más" opens the secondary list.
  const mobilePrimary: NavSpec[] = isAdmin ? adminPrimary : userMobile;
  const mobileSecondary: NavSpec[] = isAdmin ? adminSecondary : userSecondary;

  return (
    <div className="min-h-screen bg-brand-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-3 px-4 py-3 sm:gap-6 sm:px-6 sm:py-4">
          <Link
            to={isAdmin ? "/admin" : "/mis-vuelos"}
            className="inline-flex items-center gap-2.5"
          >
            <span
              aria-hidden
              className="block h-7 w-1 rounded-sm bg-brand-700"
            />
            <span className="flex flex-col leading-none">
              <span className="text-base font-bold tracking-tight text-brand-900">
                AltaJet
              </span>
              <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Vuelos
              </span>
            </span>
            {isAdmin ? (
              <span className="ml-1 rounded bg-brand-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                Admin
              </span>
            ) : null}
          </Link>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-600">
            <span className="hidden font-mono sm:inline">
              {usuario?.email}
            </span>
            {realIsAdmin ? (
              viewAsUser ? (
                <button
                  type="button"
                  onClick={exitViewAsUser}
                  className="rounded-md border-2 border-amber-400 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  title="Salir de vista usuario"
                >
                  ← Volver a admin
                </button>
              ) : (
                <button
                  type="button"
                  onClick={enterViewAsUser}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
                  title="Ver la app como la vería un usuario (no-admin)"
                >
                  Vista usuario
                </button>
              )
            ) : null}
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-brand-50"
            >
              Cerrar sesión
            </button>
          </div>
          {/* Desktop: full inline nav. Hidden on mobile (bottom bar takes over). */}
          <div className="hidden w-full md:block">
            <Nav items={desktopNav} />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-screen-2xl px-4 py-6 pb-24 sm:px-6 sm:py-8 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom bar. Hidden on md+. */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 grid border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
        style={{
          gridTemplateColumns: `repeat(${mobilePrimary.length + (mobileSecondary.length > 0 ? 1 : 0)}, 1fr)`,
        }}
      >
        {mobilePrimary.map((item) =>
          item.highlight ? (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="flex flex-col items-center gap-0.5 py-2 text-[10px]"
            >
              <span className="flex flex-col items-center gap-0.5 rounded-lg border border-brand-700 bg-brand-50 px-3 py-1 font-semibold text-brand-700">
                {item.img ? (
                  <img src={item.img} alt="" className="h-[1.35rem] w-[1.35rem]" />
                ) : item.icon ? (
                  <span className="text-base leading-none">{item.icon}</span>
                ) : null}
                <span>{item.label}</span>
              </span>
            </NavLink>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                "relative flex flex-col items-center gap-0.5 py-2 text-[10px] transition " +
                (isActive
                  ? "font-semibold text-brand-700 before:absolute before:inset-x-3 before:top-0 before:h-0.5 before:bg-brand-700"
                  : "text-slate-500 hover:text-slate-900")
              }
            >
              {item.img ? (
                <img src={item.img} alt="" className="h-[1.35rem] w-[1.35rem]" />
              ) : item.icon ? (
                <span className="text-lg leading-none">{item.icon}</span>
              ) : null}
              <span>{item.label}</span>
            </NavLink>
          )
        )}
        {mobileSecondary.length > 0 ? (
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center gap-0.5 py-2 text-[10px] text-slate-500 hover:text-slate-900"
          >
            <span className="text-lg leading-none">⋯</span>
            <span>Más</span>
          </button>
        ) : null}
      </nav>

      {/* Más sheet (mobile-only) */}
      {moreOpen ? (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 md:hidden"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white p-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mx-auto mb-3 block h-1 w-9 rounded-full bg-slate-300" />
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Más secciones
            </h3>
            <ul className="mt-2 divide-y divide-slate-100">
              {mobileSecondary.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      "flex items-center gap-2 px-1 py-3 text-base " +
                      (isActive
                        ? "font-semibold text-sky-800"
                        : "text-slate-800")
                    }
                  >
                    {item.img ? (
                      <img src={item.img} alt="" className="h-5 w-5" />
                    ) : null}
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface NavSpec {
  to: string;
  label: string;
  icon?: string;
  /** Optional image (e.g. a logo) shown before the label in nav lists. */
  img?: string;
  end?: boolean;
  highlight?: boolean;
}

function Nav({ items }: { items: NavSpec[] }) {
  return (
    <nav className="-mx-4 flex w-full items-center gap-1 overflow-x-auto px-4 text-sm sm:mx-0 sm:px-0 no-scrollbar">
      {items.map((item) =>
        item.highlight ? (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              "relative whitespace-nowrap rounded-md border border-brand-700 px-3 py-1.5 font-semibold transition " +
              (isActive
                ? "bg-brand-100 text-brand-900"
                : "bg-brand-50 text-brand-700 hover:bg-brand-100")
            }
          >
            {item.label}
          </NavLink>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              "relative inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 transition " +
              (isActive
                ? "font-medium text-brand-700 after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-brand-700"
                : "text-slate-600 hover:text-slate-900")
            }
          >
            {item.img ? (
              <img src={item.img} alt="" className="h-4 w-4" />
            ) : null}
            {item.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

/** Wrap any route that should only be visible to admins. */
export function AdminOnly({ children }: { children: ReactNode }) {
  const { usuario } = useAuth();
  const [viewAsUser] = useViewAsUser();
  const realIsAdmin = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
  if (!realIsAdmin || viewAsUser) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900">
        Esta sección es solo para administradores.
      </div>
    );
  }
  return <>{children}</>;
}
